import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Sparkles, Users, Compass, Loader2, Check } from 'lucide-react';
import api from '../lib/api';
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  getExistingSubscription,
} from '../lib/push';

/**
 * Q33 Phase A — Smart Nudges preferences screen.
 *
 * Three groups (essential / social / discovery) with per-category toggles.
 * Essential = Priority 1 (always sent, but user can still mute).
 * Social + Discovery are throttled by the engine (caps + cooldowns + quiet hours).
 *
 * Also exposes:
 *   • Browser push enable/disable (re-uses /lib/push helpers)
 *   • Timezone display (auto-set from Intl.DateTimeFormat on first save)
 */
const GROUP_META = {
  essential: {
    title: 'Essential',
    subtitle: 'Always delivered when on. ASAP will not throttle these.',
    icon: Bell,
  },
  social: {
    title: 'Social',
    subtitle: 'Followers, follow-back, communities. Throttled to feel friendly.',
    icon: Users,
  },
  discovery: {
    title: 'Discovery',
    subtitle: 'Nearby, FOMO, digests. Quiet hours respected automatically.',
    icon: Compass,
  },
};

function Toggle({ checked, onChange, testId, disabled }) {
  return (
    <button
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0
        ${checked ? 'bg-[#B6E600]' : 'bg-[#E5E4DE]'}
        ${disabled ? 'opacity-40' : ''}`}
    >
      <div
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform
          ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

export default function SettingsNotifications() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [pushAllowed, setPushAllowed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    api.get('/notifications/prefs').then(r => setData(r.data)).catch(() => setData(null));
    (async () => {
      try {
        const sub = await getExistingSubscription();
        setPushAllowed(Boolean(sub));
      } catch { setPushAllowed(false); }
    })();
  }, []);

  // Auto-store user's timezone on first visit (silently; user can override later)
  useEffect(() => {
    if (!data) return;
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && browserTz !== data.timezone) {
        api.put('/notifications/prefs', { timezone: browserTz })
          .then(r => setData(r.data))
          .catch(() => {});
      }
    } catch { /* Safari old / non-Intl envs */ }
  }, [data?.timezone]);

  const groups = useMemo(() => {
    if (!data) return [];
    const by = { essential: [], social: [], discovery: [] };
    for (const cat of data.categories) {
      (by[cat.group] || by.discovery).push(cat);
    }
    return ['essential', 'social', 'discovery'].map(k => ({ key: k, ...GROUP_META[k], items: by[k] }));
  }, [data]);

  const toggle = async (key, value) => {
    setSavingKey(key);
    // Optimistic
    setData(d => ({ ...d, values: { ...d.values, [key]: value } }));
    try {
      const r = await api.put('/notifications/prefs', { notification_prefs: { [key]: value } });
      setData(r.data);
    } catch {
      // Revert on failure
      setData(d => ({ ...d, values: { ...d.values, [key]: !value } }));
    }
    setSavingKey(null);
  };

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushAllowed) {
        await unsubscribeFromPush();
        setPushAllowed(false);
      } else {
        const ok = await subscribeToPush();
        setPushAllowed(Boolean(ok));
      }
    } catch { /* user denied or unsupported */ }
    setPushBusy(false);
  };

  if (!data) {
    return (
      <div className="content-area pb-12 flex items-center justify-center min-h-[60vh]" data-testid="notif-settings-loading">
        <Loader2 className="animate-spin text-[#9B9EA3]" />
      </div>
    );
  }

  return (
    <div className="content-area pb-16" data-testid="settings-notifications-page">
      <header className="px-4 pt-5 pb-4 flex items-center gap-3">
        <button
          data-testid="notif-settings-back"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:bg-[#F1F0EA]"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-extrabold text-[#111] tracking-tight">Notifications</h1>
          <p className="text-[11px] text-[#9B9EA3]">{data.timezone}</p>
        </div>
      </header>

      {isPushSupported() && (
        <section className="px-4 mb-5">
          <div
            data-testid="notif-push-row"
            className="bg-white rounded-2xl p-4 flex items-center gap-3 border border-black/[0.06]"
          >
            <div className="w-10 h-10 rounded-xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-[#111]">Push notifications</p>
              <p className="text-[11px] text-[#6B6E73]">
                {pushAllowed ? 'Enabled in this browser' : 'Get nudged when something important happens'}
              </p>
            </div>
            <button
              data-testid={pushAllowed ? 'notif-push-disable' : 'notif-push-enable'}
              onClick={togglePush}
              disabled={pushBusy}
              className={`px-3 py-1.5 rounded-full text-xs font-extrabold flex-shrink-0
                ${pushAllowed ? 'bg-[#F1F0EA] text-[#111]' : 'bg-[#B6E600] text-[#111]'}
                ${pushBusy ? 'opacity-50' : 'active:scale-[0.96]'}`}
            >
              {pushBusy ? '…' : (pushAllowed ? 'Disable' : 'Enable')}
            </button>
          </div>
        </section>
      )}

      <div className="px-4 space-y-5">
        {groups.map(g => (
          <section key={g.key} data-testid={`notif-group-${g.key}`}>
            <div className="flex items-center gap-2 mb-2 px-1">
              <g.icon size={14} className="text-[#3F5200]" strokeWidth={2.6} />
              <h2 className="text-[11px] font-extrabold uppercase tracking-wider text-[#3F5200]">{g.title}</h2>
            </div>
            <p className="text-[11px] text-[#9B9EA3] mb-2 px-1 leading-relaxed">{g.subtitle}</p>
            <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]">
              {g.items.map(cat => (
                <div
                  key={cat.key}
                  data-testid={`notif-row-${cat.key}`}
                  className="flex items-center gap-3 p-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#111]">{cat.label}</p>
                    {cat.essential && (
                      <p className="text-[10px] text-[#3F5200] font-bold uppercase tracking-wider mt-0.5">P1 · always sent</p>
                    )}
                  </div>
                  {savingKey === cat.key && <Loader2 size={14} className="animate-spin text-[#9B9EA3]" />}
                  <Toggle
                    checked={data.values[cat.key]}
                    onChange={(v) => toggle(cat.key, v)}
                    testId={`notif-toggle-${cat.key}`}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}

        <section data-testid="notif-rules-card" className="bg-[#F1F0EA] rounded-2xl border border-black/[0.04] p-4 text-[11px] text-[#6B6E73] leading-relaxed">
          <div className="flex items-center gap-2 text-[#111] font-extrabold text-xs mb-2">
            <Check size={14} className="text-[#3F5200]" />
            How ASAP decides
          </div>
          <ul className="space-y-1 list-disc pl-4">
            <li>P1 (essential) bypasses every limit.</li>
            <li>Max 2 marketing pushes per 24h · 1 per 6h.</li>
            <li>Quiet hours 23:00–07:00 in your timezone.</li>
            <li>Each category has its own cooldown to feel friendly.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
